import {createClient,type SupabaseClient} from '@supabase/supabase-js';
import {randomName,checkName} from '../names.mjs';
import {randomFreeAvatarId,isAvatarId,type AvatarId} from '../avatars.mjs';

export type ViewerProfile={playerId:string;name:string;skin:AvatarId;named:boolean;authenticated:boolean;email:string|null;expiresAt?:string};
type Pending={name?:string;skin?:AvatarId};
const PENDING='infinite:pending-profile';
class Viewer {
  profile:ViewerProfile|null=null;
  client:SupabaseClient|null=null;
  google=false;
  available=true;
  error='';
  private starting:Promise<ViewerProfile>|null=null;
  private listeners=new Set<()=>void>();
  private channel=typeof BroadcastChannel!=='undefined'?new BroadcastChannel('infinite:profile'):null;
  constructor(){
    if(this.channel)this.channel.onmessage=()=>{void this.refresh().catch(()=>{});};
  }
  subscribe(fn:()=>void){this.listeners.add(fn);return()=>{this.listeners.delete(fn);};}
  private emit(){for(const fn of this.listeners)fn();}
  async request(path:string,method='GET',data?:unknown,token?:string){
    const r=await fetch('/api/'+path,{method,credentials:'same-origin',cache:'no-store',headers:{...(data?{'content-type':'application/json'}:{}),...(token?{authorization:'Bearer '+token}:{})},body:data?JSON.stringify(data):undefined,signal:AbortSignal.timeout(12000)});
    const result=await r.json().catch(()=>({}));
    if(!r.ok)throw Error(result.error||'Could not connect. Please try again.');return result;
  }
  async ready():Promise<ViewerProfile>{
    if(this.profile)return this.profile;
    if(!this.starting)this.starting=this.start().catch(e=>{this.starting=null;throw e;});
    return this.starting;
  }
  private async start():Promise<ViewerProfile>{
    try{this.profile=await this.request('session','POST');}
    catch(e){
      if(!import.meta.env.DEV)throw e;
      const query=new URLSearchParams(location.search),name=query.get('name'),skin=query.get('skin');
      this.available=false;this.profile={playerId:'local-preview',name:name&&checkName(name)==='ok'?name:randomName(),skin:isAvatarId(skin)?skin:randomFreeAvatarId(),named:false,authenticated:false,email:null};
      return this.profile;
    }
    try{
      const config=await this.request('viewer/config');this.google=config.google;
      if(config.url&&config.key){
        this.client=createClient(config.url,config.key,{auth:{storageKey:'infinite-viewer-auth',flowType:'pkce',detectSessionInUrl:false}});
        const url=new URL(location.href);
        const hash=new URLSearchParams(url.hash.startsWith('#')?url.hash.slice(1):'');
        if(hash.get('token_hash')){
          const type=hash.get('type');
          url.hash='';url.searchParams.delete('auth');history.replaceState(null,'',url.pathname+url.search);
          const {data,error}=await this.client.auth.verifyOtp({token_hash:hash.get('token_hash')!,type:type==='invite'||type==='signup'||type==='email'?type:'magiclink'});
          if(error||!data.session)throw Error('This sign-in link has expired. Please try again.');
          await this.link(data.session.access_token);
        } else if(url.searchParams.has('code')&&url.searchParams.get('auth')==='return'){
          const code=url.searchParams.get('code')!;
          url.searchParams.delete('code');url.searchParams.delete('auth');history.replaceState(null,'',url.pathname+url.search+url.hash);
          const {data,error}=await this.client.auth.exchangeCodeForSession(code);
          if(error||!data.session)throw Error('This sign-in link has expired. Please try again.');
          await this.link(data.session.access_token);
        } else {
          if(url.searchParams.get('auth')==='return'){
            url.searchParams.delete('auth');url.searchParams.delete('error');url.searchParams.delete('error_description');url.hash='';history.replaceState(null,'',url.pathname+url.search);
            this.error='Sign-in was cancelled. You can try again.';
          }
          const {data}=await this.client.auth.getSession();
          if(data.session&&!this.profile!.authenticated)await this.link(data.session.access_token);
        }
      }
    }catch(e){this.error=e instanceof Error?e.message:'Sign-in failed. Please try again.';}
    this.emit();return this.profile!;
  }
  async refresh(){this.profile=await this.request('session','POST');this.emit();return this.profile!;}
  async save(input:Pending){
    await this.ready();
    this.profile=await this.request('viewer/profile','PUT',input);this.emit();this.channel?.postMessage('changed');return this.profile!;
  }
  pending():Pending|null {try{return JSON.parse(sessionStorage.getItem(PENDING)||'null');}catch{return null;}}
  rememberPending(input:Pending){try{sessionStorage.setItem(PENDING,JSON.stringify(input));}catch{throw Error('Allow browser storage to save your profile.');}}
  clearPending(){try{sessionStorage.removeItem(PENDING);}catch{/* Storage is optional after completion. */}}
  async resume(){const pending=this.pending();if(pending&&this.profile?.authenticated){await this.save(pending);this.clearPending();}}
  async link(token:string){
    this.profile=await this.request('viewer/link','POST',{},token);this.channel?.postMessage('changed');this.emit();
    // A name conflict must not undo a successful login. The pending name remains editable.
    try{await this.resume();}catch(e){this.error=e instanceof Error?e.message:'Choose another name.';}
  }
  async sendCode(email:string){
    await this.ready();if(!this.client)throw Error('Sign-in is temporarily unavailable.');
    const {error}=await this.client.auth.signInWithOtp({email,options:{emailRedirectTo:location.origin+'/?auth=return'}});
    if(error)throw Error(error.status===429?'Please wait a minute before requesting another code.':'We could not send your code. Check the address and try again.');
  }
  async verifyCode(email:string,token:string){
    if(!this.client)throw Error('Sign-in is temporarily unavailable.');
    const {data,error}=await this.client.auth.verifyOtp({email,token,type:'email'});
    if(error||!data.session)throw Error('This code is invalid or expired. Try again or request a new code.');
    await this.link(data.session.access_token);
  }
  async signInGoogle(){
    await this.ready();if(!this.client)throw Error('Sign-in is temporarily unavailable.');
    const url=new URL(location.href);url.searchParams.set('auth','return');url.hash='';
    const {error}=await this.client.auth.signInWithOAuth({provider:'google',options:{redirectTo:url.href}});
    if(error)throw Error('Google sign-in is unavailable. Try your email instead.');
  }
  async signOut(){
    await this.request('session','DELETE');
    if(this.client)await this.client.auth.signOut({scope:'local'});
    this.clearPending();this.profile=null;this.starting=null;
    await this.refresh();this.channel?.postMessage('changed');
  }
}
export const viewer=new Viewer();
